import { WbfPolicies } from '@workers-benefit-fund/shared-data'

import {
  RouteConfig,
  DEFAULT_ROUTES as default_routes,
} from '@v2/domains/routing/types/routing.types'
import { SMS_CAMPAIGN_UPDATE_POLICY } from '@v2/domains/sms-campaigns/sms-campaigns.types'

export const PUBLIC_ROUTES_CONFIG: Record<
  string,
  Omit<RouteConfig, 'element'>
> = {
  LOGIN: {
    key: 'LOGIN',
    path: '/login',
    requiresAuth: false,
    label: 'Login',
    showInMenu: false,
  },
  FORGOT_PASSWORD: {
    key: 'FORGOT_PASSWORD',
    path: '/forgot',
    requiresAuth: false,
    label: 'Forgot my password',
    showInMenu: false,
  },
  SELECT_ORGANIZATION: {
    key: 'SELECT_ORGANIZATION',
    path: '/select-organization',
    requiresAuth: true,
    label: 'Select Organization',
    showInMenu: false,
  },
  RESET_PASSWORD: {
    key: 'RESET_PASSWORD',
    path: '/reset-password',
    requiresAuth: false,
    label: 'Reset Password',
    showInMenu: false,
  },
  SET_UP_PASSKEY: {
    key: 'SET_UP_PASSKEY',
    path: '/set-up-passkey',
    requiresAuth: false,
    label: 'Set Up Passkey',
    showInMenu: false,
  },
  VERIFY_PASSKEY: {
    key: 'VERIFY_PASSKEY',
    path: '/verify-passkey',
    requiresAuth: false,
    label: 'Verify Passkey',
    showInMenu: false,
  },
  EXPIRED_PASSKEY: {
    key: 'EXPIRED_PASSKEY',
    path: '/expired-passkey',
    requiresAuth: false,
    label: 'Expired Passkey',
    showInMenu: false,
  },
}

export const DEFAULT_ROUTES_CONFIG: Record<
  string,
  Omit<RouteConfig, 'element'>
> = {
  [default_routes.NOTIFICATIONS]: {
    key: default_routes.NOTIFICATIONS,
    path: '/notifications',
    requiresAuth: true,
    label: 'Notifications',
    policy: 'is_web_push_registration_enabled',
    showInMenu: true,
    menuConfig: { icon: 'Bell', order: 30, platforms: ['default'] },
  },
  [default_routes.DASHBOARD]: {
    key: 'DASHBOARD',
    path: '/dashboard',
    requiresAuth: true,
    label: 'Dashboard',
    policy: 'is_get_dashboard_enabled',
    showInMenu: false,
    menuConfig: {
      icon: 'Reports',
      order: 1,
      platforms: ['default'],
    },
  },
  [default_routes.LEADS]: {
    key: 'LEADS',
    path: '/leads',
    requiresAuth: true,
    label: 'Leads',
    policy: 'is_get_persons_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'User',
      order: 3,
      platforms: ['default'],
    },
  },
  [default_routes.LEAD_DETAIL]: {
    key: 'LEAD_DETAIL',
    path: '/leads/:personId',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  [default_routes.LEAD_DETAIL_V1]: {
    key: 'LEAD_DETAIL_V1',
    path: '/v1/leads/:personId',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  [default_routes.LEAD_CREATE]: {
    key: 'LEAD_CREATE',
    path: '/leads/create',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  [default_routes.ACCOUNTS]: {
    key: 'ACCOUNTS',
    path: '/accounts',
    requiresAuth: true,
    label: 'Accounts',
    policy: 'is_get_persons_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Account',
      order: 4,
      platforms: ['default'],
    },
  },
  [default_routes.ACCOUNT_DETAIL]: {
    key: 'ACCOUNT_DETAIL',
    path: '/accounts/:personId',
    requiresAuth: true,
    label: 'Account Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'ACCOUNTS',
  },
  [default_routes.ACCOUNT_DETAIL_V1]: {
    key: 'ACCOUNT_DETAIL_V1',
    path: '/v1/accounts/:personId',
    requiresAuth: true,
    label: 'Account Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'ACCOUNTS',
  },
  [default_routes.MERGING_TOOL]: {
    key: 'MERGING_TOOL',
    path: '/merging-tool',
    requiresAuth: true,
    label: 'Merging tool',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'ACCOUNTS',
  },
  [default_routes.TICKETS]: {
    key: 'TICKETS',
    path: '/tickets',
    requiresAuth: true,
    label: 'Tickets',
    policy: 'is_get_office_visits_by_organization_id_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Ticket',
      order: 2,
      platforms: ['default'],
    },
  },
  [default_routes.TASKS]: {
    key: 'TASKS',
    path: '/tasks',
    requiresAuth: true,
    label: 'Tasks',
    policy: 'is_get_tasks_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Page',
      order: 9,
      platforms: ['default'],
    },
  },
  [default_routes.ESCALATIONS]: {
    key: 'ESCALATIONS',
    path: '/escalations',
    requiresAuth: true,
    label: 'Escalations',
    policy: 'is_escalations_table_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Escalation',
      order: 10,
      platforms: ['default'],
    },
  },
  [default_routes.WEB_SITES]: {
    key: 'WEB_SITES',
    path: '/web-sites',
    requiresAuth: true,
    label: 'Web Sites',
    policy: WbfPolicies.is_get_organization_web_sites_enabled,
    showInMenu: true,
    menuConfig: {
      icon: 'Telemedicine',
      order: 11,
      platforms: ['default'],
    },
  },
  [default_routes.ADMINISTRATION]: {
    key: 'ADMINISTRATION',
    path: '/administration',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: true,
    menuConfig: {
      icon: 'Benefit',
      order: 11,
    },
  },
  [default_routes.ADMINISTRATION_ABCS]: {
    key: 'ADMINISTRATION_ABCS',
    path: '/administration/abcs',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_POLICIES]: {
    key: 'ADMINISTRATION_POLICIES',
    path: '/administration/policies',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_ROLES]: {
    key: 'ADMINISTRATION_ROLES',
    path: '/administration/roles',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_ROLES_DETAIL]: {
    key: 'ADMINISTRATION_ROLES_DETAIL',
    path: '/administration/roles/:roleId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_TAGS]: {
    key: 'ADMINISTRATION_TAGS',
    path: '/administration/tags',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_USERS]: {
    key: 'ADMINISTRATION_USERS',
    path: '/administration/users',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_USERS_CREATE]: {
    key: 'ADMINISTRATION_USERS_CREATE',
    path: '/administration/users/create',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_USERS_DETAILS]: {
    key: 'ADMINISTRATION_USERS_DETAILS',
    path: '/administration/users/:userId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_EMAIL_TEMPLATE]: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE',
    path: '/administration/email-template',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_EMAIL_TEMPLATE_DETAIL]: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE_DETAIL',
    path: '/administration/email-template/:emailTemplateId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_EMAIL_TEMPLATE_CREATE]: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE_CREATE',
    path: '/administration/email-template/create',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_FORM_BUILDER]: {
    key: 'ADMINISTRATION_FORM_BUILDER',
    path: '/administration/form-builder',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.REPORTS]: {
    key: 'REPORTS',
    path: '/reports',
    requiresAuth: true,
    label: 'Reports',
    policy: 'is_enable_reports',
    showInMenu: true,
    menuConfig: {
      icon: 'Reports',
      order: 12,
      platforms: ['default'],
    },
  },
  [default_routes.DATA_PROCESSING]: {
    key: 'DATA_PROCESSING',
    path: '/data-processing',
    requiresAuth: true,
    label: 'Data Processing',
    policy: 'is_enable_data_processing',
    showInMenu: true,
    menuConfig: {
      icon: 'Reports',
      order: 13,
      platforms: ['default'],
    },
  },
  [default_routes.SEARCH]: {
    key: 'SEARCH',
    path: '/search/:searchValue',
    requiresAuth: true,
    label: '',
    policy: 'is_enable_data_processing',
    showInMenu: false,
  },
  [default_routes.SMS_CAMPAIGNS]: {
    key: 'SMS_CAMPAIGNS',
    path: '/sms-campaigns',
    requiresAuth: true,
    label: 'SMS Campaigns',
    policy: 'is_sms_campaigns_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Sms',
      order: 7,
      platforms: ['default'],
    },
  },
  [default_routes.SMS_CAMPAIGNS_CREATE]: {
    key: 'SMS_CAMPAIGNS_CREATE',
    path: '/sms-campaigns/create',
    requiresAuth: true,
    label: 'SMS Campaigns',
    policy: 'is_sms_campaigns_enabled',
    showInMenu: false,
    parentKey: 'SMS_CAMPAIGNS',
  },
  [default_routes.SMS_CAMPAIGNS_DETAIL]: {
    key: 'SMS_CAMPAIGNS_DETAIL',
    path: '/sms-campaigns/:campaignId',
    requiresAuth: true,
    label: 'SMS Campaigns',
    policy: 'is_sms_campaigns_enabled',
    showInMenu: false,
    parentKey: 'SMS_CAMPAIGNS',
  },
  [default_routes.COMMUNICATIONS_HUB_SMS_CAMPAIGN_CREATE]: {
    key: 'COMMUNICATIONS_HUB_SMS_CAMPAIGN_CREATE',
    path: '/communications-hub/sms-campaign/create',
    requiresAuth: true,
    label: 'Create SMS Campaign',
    policy: WbfPolicies.is_sms_campaigns_enabled,
    showInMenu: false,
    parentKey: 'COMMUNICATIONS_HUB',
  },
  [default_routes.COMMUNICATIONS_HUB_SMS_CAMPAIGN_EDIT]: {
    key: 'COMMUNICATIONS_HUB_SMS_CAMPAIGN_EDIT',
    path: '/communications-hub/sms-campaign/:campaignId/edit',
    requiresAuth: true,
    label: 'Edit SMS Campaign',
    policy: SMS_CAMPAIGN_UPDATE_POLICY,
    showInMenu: false,
    parentKey: 'COMMUNICATIONS_HUB_SMS_CAMPAIGN',
  },
  [default_routes.COMMUNICATIONS_HUB_SMS_CAMPAIGN_TODO]: {
    key: 'COMMUNICATIONS_HUB_SMS_CAMPAIGN_TODO',
    path: '/communications-hub/sms-campaign/todo',
    requiresAuth: true,
    label: 'SMS Campaign TODO',
    policy: 'is_get_campaigns_enabled',
    showInMenu: false,
    parentKey: 'COMMUNICATIONS_HUB',
  },
  [default_routes.COMMUNICATIONS_HUB]: {
    key: 'COMMUNICATIONS_HUB',
    path: '/communications-hub',
    requiresAuth: true,
    label: 'Comms Hub',
    showInMenu: true,
    policy: 'is_get_campaigns_enabled',
    menuConfig: {
      icon: 'Chat',
      order: 6,
      platforms: ['default'],
    },
  },
  [default_routes.COMMUNICATIONS_HUB_SMS_CAMPAIGN]: {
    key: 'COMMUNICATIONS_HUB_SMS_CAMPAIGN',
    path: '/communications-hub/sms-campaign',
    requiresAuth: true,
    label: 'SMS Campaign',
    policy: 'is_get_campaigns_enabled',
    showInMenu: false,
    parentKey: 'COMMUNICATIONS_HUB',
  },
  [default_routes.COMMUNICATIONS_HUB_SMS_CAMPAIGN_DETAIL]: {
    key: 'COMMUNICATIONS_HUB_SMS_CAMPAIGN_DETAIL',
    path: '/communications-hub/sms-campaign/:campaignId',
    requiresAuth: true,
    label: 'SMS Campaign',
    policy: 'is_get_campaigns_enabled',
    showInMenu: false,
    parentKey: 'COMMUNICATIONS_HUB_SMS_CAMPAIGN',
  },
  [default_routes.FORM_SUBMISSIONS]: {
    key: 'FORM_SUBMISSIONS',
    path: '/form-submissions',
    requiresAuth: true,
    label: 'Forms Submission',
    policy: WbfPolicies.is_enable_view_form_submissions_tab,
    showInMenu: true,
    menuConfig: {
      icon: 'PageDuplicate',
      order: 8,
      platforms: ['default'],
    },
  },
  [default_routes.FORM_SUBMISSIONS_DETAIL]: {
    key: 'FORM_SUBMISSIONS_DETAIL',
    path: '/form-submissions/:formId',
    requiresAuth: true,
    label: 'Forms Submission',
    policy: WbfPolicies.is_enable_view_form_submissions_tab,
    showInMenu: false,
    parentKey: 'FORM_SUBMISSIONS',
  },
  [default_routes.UPLOAD_FILE]: {
    key: 'UPLOAD_FILE',
    path: '/upload-file',
    requiresAuth: true,
    label: 'Upload File',
    policy: WbfPolicies.is_enable_view_form_submissions_tab,
    showInMenu: false,
  },
  [default_routes.TEAM_LEAD_HUB]: {
    key: 'TEAM_LEAD_HUB',
    path: '/team-lead-hub',
    requiresAuth: true,
    label: 'Team Lead Hub',
    policy: 'is_team_lead_hub_tab_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'TeamLeadHub',
      order: 5,
      platforms: ['default'],
    },
  },
  [default_routes.CLINICS]: {
    key: 'CLINICS',
    path: '/team-lead-hub/clinic',
    requiresAuth: true,
    label: 'Clinics',
    showInMenu: false,
  },
  [default_routes.CLINIC_DETAIL]: {
    key: 'CLINIC_DETAIL',
    path: '/team-lead-hub/clinic/:clinicId',
    requiresAuth: true,
    label: 'Clinic Detail',
    showInMenu: false,
  },
  [default_routes.LOCATIONS]: {
    key: 'LOCATIONS',
    path: '/team-lead-hub/locations',
    requiresAuth: true,
    label: 'Locations',
    showInMenu: false,
  },
  [default_routes.ENROLLMENT_CHECKER]: {
    key: default_routes.ENROLLMENT_CHECKER,
    path: '/enrollment-checker',
    requiresAuth: true,
    label: 'Enrollment Checker',
    showInMenu: false,
    policy: WbfPolicies.is_enrollment_checker_vendors_enabled,
  },
  [default_routes.RECENT_VIEWED]: {
    key: default_routes.RECENT_VIEWED,
    path: '/recent-viewed',
    requiresAuth: true,
    label: 'Recent',
    showInMenu: true,
    policy: WbfPolicies.is_last_visited_profiles_enabled,
    menuConfig: {
      icon: 'Recent',
      order: 1,
      platforms: ['default'],
    },
  },
  [default_routes.LIST_BUILDER]: {
    key: 'LIST_BUILDER',
    path: '/list-builder',
    requiresAuth: true,
    label: 'Lists',
    showInMenu: true,
    policy: WbfPolicies.is_get_lists_enabled,
    menuConfig: {
      icon: 'Lists',
      order: 14,
      platforms: ['default'],
    },
  },
  [default_routes.LIST_BUILDER_CREATE]: {
    key: 'LIST_BUILDER_CREATE',
    path: '/list-builder/create',
    requiresAuth: true,
    label: 'Create List',
    showInMenu: false,
    parentKey: 'LIST_BUILDER',
    policy: WbfPolicies.is_get_lists_enabled,
  },
  [default_routes.LIST_BUILDER_DETAIL]: {
    key: 'LIST_BUILDER_DETAIL',
    path: '/list-builder/:listId',
    requiresAuth: true,
    label: 'List Details',
    showInMenu: false,
    parentKey: 'LIST_BUILDER',
    policy: WbfPolicies.is_get_lists_enabled,
  },
}

export const CUSTOM_ROUTES_CONFIG: Omit<RouteConfig, 'element'> = {
  key: 'CUSTOM_PATH',
  path: '',
  requiresAuth: true,
  label: 'ROUTE FOR LEGACY NAVIGATION',
  showInMenu: false,
}

export const ALL_ROUTES: Record<string, Omit<RouteConfig, 'element'>> = {
  ...PUBLIC_ROUTES_CONFIG,
  ...DEFAULT_ROUTES_CONFIG,
  CUSTOM_PATH: CUSTOM_ROUTES_CONFIG,
}
